import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ConditionalWrapper, cn } from "@/lib/utils";
import { Loader2, LucideIcon } from "lucide-react";
import React from "react";
import {
  ControllerRenderProps,
  FieldValues,
  Path,
  useForm,
  UseFormReturn,
} from "react-hook-form";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { type DropzoneFieldProps } from "./ui/dropzone-field";
import { EmptySpace } from "./ui/empty-space";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import FormattedNumberField from "./ui/formatted-number-field";
import { MultiSelect } from "./ui/multi-select-field";
import DecimalInput from "./ui/decimal-input";
import { MultiSelectAlt } from "./ui/multi-select-field-alt";
import { SelectSearch } from "./ui/select-search";
import { Separator } from "./ui/separator";
import { FileUploadDropzone } from "./ui/file-upload-dropzone";

const DropzoneField = React.lazy(() => import("./ui/dropzone-field"));
const DatePicker = React.lazy(() =>
  import("./ui/date-picker").then((module) => ({
    default: module.DatePicker,
  })),
);
const DatePickerAlt = React.lazy(() =>
  import("./ui/date-picker-alt").then((module) => ({
    default: module.DatePicker,
  })),
);

const Required = () => (
  <>
    <span className="sr-only">(Required)</span>
    <span className="font-bold text-red-500">*</span>
  </>
);

interface FormFieldOptionTemplate {
  label: string;
  value: string;
  description?: string;
}

export interface FormFieldTemplate<FV extends FieldValues = any> {
  name?:
    | string
    | ((form: UseFormReturn<FV>, field: ControllerRenderProps<FV>) => string);
  showTopLabel?: boolean;
  labelClassName?: string;
  placeholder?: string;
  takeWholeRow?: boolean;
  className?: string | ((form: UseFormReturn<FV>) => string);
  type?: string;
  label?:
    | string
    | ((form: UseFormReturn<FV>, field: ControllerRenderProps<FV>) => string);
  customComponent?: JSX.Element | ((form: UseFormReturn<FV>) => JSX.Element);
  disabled?:
    | boolean
    | ((form: UseFormReturn<FV>, field: ControllerRenderProps<FV>) => boolean);
  required?: boolean;
  leftIcon?: LucideIcon;
  rightIcon?: LucideIcon;
  options?:
    | FormFieldOptionTemplate[]
    | ((
        form: UseFormReturn<FV>,
        field: ControllerRenderProps<FV>,
      ) => FormFieldOptionTemplate[]);
  dropzoneOptions?: DropzoneFieldProps<FV>;
  step?: string;
  extra?: React.ComponentPropsWithoutRef<
    typeof Input | typeof Checkbox | typeof DecimalInput
  >;
  extraProps?: (
    form: UseFormReturn<FV>,
    field: ControllerRenderProps<FV>,
  ) =>
    | React.ComponentPropsWithoutRef<
        typeof Input | typeof Checkbox | typeof DecimalInput
      >
    | Omit<
        React.ComponentPropsWithoutRef<typeof SelectSearch>,
        "options" | "onChange"
      >;
  helpText?: string | ((form: UseFormReturn<FV>) => string);
}

export interface FormSectionTemplate<FV extends FieldValues = any> {
  title?: string;
  fields: FormFieldTemplate<FV>[];
  collapsibe?: boolean;
  collapsibleText?: string;
}

export interface FormTemplateProps<FV extends FieldValues> {
  form: ReturnType<typeof useForm<FV>>;
  className?: string;
  submitLabel?: string;
  sections: FormSectionTemplate[];
  sectionTitleSeparator?: boolean;
  onSubmit?: (data: FV) => Promise<void>;
  children?: React.ReactNode;
  showErrors?: boolean;
  isLoading?: boolean;
  showSubmitButton?: boolean;
  submitButtonClassName?: string;
  mode?: "onBlur" | "onChange" | "onSubmit" | "onTouched" | undefined;
  displayOnly?: boolean;
}
const ROW_CLASS = "col-span-12 grid grid-cols-12 gap-4";

const FormTemplateInner = <FV extends FieldValues>(
  {
    form,
    sections,
    sectionTitleSeparator = false,
    onSubmit: onSubmitProp,
    className,
    showErrors = true,
    isLoading,
    submitLabel,
    showSubmitButton = true,
    submitButtonClassName = "mt-8",
    displayOnly = false,
    ...rest
  }: FormTemplateProps<FV>,
  ref: React.ForwardedRef<any>,
) => {
  const formRef = React.useRef<HTMLFormElement>(null);

  const onSubmit = form.handleSubmit(async (data) => {
    form.clearErrors();
    if (onSubmitProp) {
      return await onSubmitProp?.(data);
    }
  });

  React.useImperativeHandle(
    ref,
    () => ({
      handleSubmit: () =>
        formRef.current?.dispatchEvent(
          new Event("submit", { cancelable: true, bubbles: true }),
        ),
    }),
    [formRef],
  );

  const [currentSection, setCurrentSection] = React.useState<
    string | undefined
  >("Basic Information");

  React.useEffect(() => {
    setCurrentSection(sections[0]?.title);
  }, [sections]);

  const [sectionsWithErrors, setSectionsWithErrors] = React.useState<
    Array<string | undefined>
  >([]);

  React.useEffect(() => {
    const errorFields = Object.keys(form.formState.errors);
    if (errorFields.length) {
      setSectionsWithErrors(
        sections
          .filter((section) => {
            return section.fields.some((field) => {
              return (
                typeof field.name === "string" &&
                errorFields.includes(field.name) // TODO handle functions
              );
            });
          })
          .map((section) => section.title),
      );
    } else {
      setSectionsWithErrors([]);
    }
  }, [form.formState.errors]);

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} ref={formRef}>
        <div
          className={cn("grid w-full grid-cols-12 gap-y-4", className)}
          {...rest}
        >
          {sections.map((sectionTemplate, sectionIdx) => (
            <div className={ROW_CLASS} key={`form-section-${sectionIdx}`}>
              <ConditionalWrapper
                condition={!!sectionTemplate.collapsibe}
                wrapper={(children) => (
                  <Accordion
                    type="single"
                    collapsible
                    className="col-span-12"
                    value={
                      currentSection === sectionTemplate.title!
                        ? sectionTemplate.title
                        : undefined
                    }
                  >
                    <AccordionItem
                      value={sectionTemplate.title!}
                      className="border-none"
                    >
                      <AccordionTrigger
                        extraText={sectionTemplate.collapsibleText}
                        className={
                          sectionsWithErrors.includes(sectionTemplate.title)
                            ? "text-red-500"
                            : ""
                        }
                        variant="form"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (currentSection !== sectionTemplate.title)
                            setCurrentSection(sectionTemplate.title);
                          else setCurrentSection(undefined);
                        }}
                      >
                        {sectionTemplate.title}
                      </AccordionTrigger>
                      <AccordionContent className={cn(ROW_CLASS, "p-1")}>
                        {children}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                )}
              >
                {sectionTemplate.title && !sectionTemplate.collapsibe && (
                  <h1 className="col-span-12 text-xl font-bold">
                    {sectionTemplate.title}
                    {sectionTitleSeparator && <Separator className="mt-1" />}
                  </h1>
                )}
                {sectionTemplate.fields.map((fieldTemplate, fieldIdx) => (
                  <ConditionalWrapper
                    condition={!!fieldTemplate.takeWholeRow}
                    wrapper={(children) => (
                      <div className={ROW_CLASS}>{children}</div>
                    )}
                    key={`form-${sectionIdx}-field-${fieldIdx}`}
                  >
                    <div
                      className={cn(
                        "col-span-12",
                        typeof fieldTemplate.className === "function"
                          ? fieldTemplate.className(form)
                          : fieldTemplate.className,
                      )}
                    >
                      {fieldTemplate.customComponent ? (
                        typeof fieldTemplate.customComponent === "function" ? (
                          fieldTemplate.customComponent(form)
                        ) : (
                          fieldTemplate.customComponent
                        )
                      ) : (
                        <FormField<FV>
                          control={form.control}
                          name={fieldTemplate.name as Path<FV>}
                          render={({ field }) => (
                            <FormItem>
                              {fieldTemplate.showTopLabel && (
                                <FormLabel
                                  className={fieldTemplate.labelClassName}
                                >
                                  {typeof fieldTemplate.label === "function"
                                    ? fieldTemplate.label(form, field)
                                    : fieldTemplate.label ||
                                      (fieldTemplate.name as string)}
                                  {fieldTemplate.required && <Required />}
                                </FormLabel>
                              )}
                              <FormControl>
                                {displayOnly ? (
                                  <div className="flex flex-row items-center space-x-2 space-y-0">
                                    {fieldTemplate.leftIcon && (
                                      <fieldTemplate.leftIcon className="h-4 w-4" />
                                    )}
                                    <span>{field.value}</span>
                                  </div>
                                ) : fieldTemplate.type === "checkbox" ? (
                                  <div className="mt-2 flex flex-row items-center space-x-2 space-y-0">
                                    {(() => {
                                      const extraProps = fieldTemplate.extraProps?.(form, field) as React.ComponentPropsWithoutRef<typeof Checkbox> | undefined;
                                      const { checked: extraChecked, ...restExtraProps } = extraProps || {};
                                      return (
                                        <Checkbox
                                          checked={extraChecked !== undefined ? extraChecked : field.value}
                                          onCheckedChange={field.onChange}
                                          disabled={
                                            typeof fieldTemplate.disabled ===
                                            "function"
                                              ? fieldTemplate.disabled(form, field)
                                              : fieldTemplate.disabled
                                          }
                                          {...restExtraProps}
                                        />
                                      );
                                    })()}
                                    <FormLabel
                                      className="cursor-pointer"
                                      onClick={() => {
                                        if (fieldTemplate.disabled) return;
                                        field.onChange(!field?.value);
                                      }}
                                    >
                                      {typeof fieldTemplate.name === "function"
                                        ? fieldTemplate.name(form, field)
                                        : (fieldTemplate.label as string) ||
                                          fieldTemplate.name}
                                    </FormLabel>
                                  </div>
                                ) : fieldTemplate.type === "textarea" ? (
                                  <Textarea
                                    placeholder={fieldTemplate.placeholder}
                                    {...field}
                                    disabled={
                                      typeof fieldTemplate.disabled ===
                                      "function"
                                        ? fieldTemplate.disabled(form, field)
                                        : fieldTemplate.disabled
                                    }
                                  />
                                ) : fieldTemplate.type === "date" ? (
                                  <DatePicker
                                    placeholder={fieldTemplate.placeholder}
                                    field={field}
                                    disabled={
                                      typeof fieldTemplate.disabled ===
                                      "function"
                                        ? fieldTemplate.disabled(form, field)
                                        : fieldTemplate.disabled
                                    }
                                    className={`${
                                      form.formState.errors[field.name] &&
                                      "border-red-500"
                                    }`}
                                  />
                                ) : fieldTemplate.type === "date-alt" ? (
                                  <DatePickerAlt
                                    placeholder={fieldTemplate.placeholder}
                                    field={field}
                                    disabled={
                                      typeof fieldTemplate.disabled ===
                                      "function"
                                        ? fieldTemplate.disabled(form, field)
                                        : fieldTemplate.disabled
                                    }
                                    className={`${
                                      form.formState.errors[field.name] &&
                                      "border-red-500"
                                    }`}
                                  />
                                ) : fieldTemplate.type === "multiselect" ? (
                                  <MultiSelect
                                    className={`${fieldTemplate.className} ${
                                      form.formState.errors[field.name] &&
                                      "border-red-500"
                                    }`}
                                    options={
                                      typeof fieldTemplate.options ===
                                      "function"
                                        ? fieldTemplate.options(form, field)
                                        : (fieldTemplate.options ?? [])
                                    }
                                    onChange={field.onChange}
                                    selected={field.value || []}
                                    disabled={
                                      typeof fieldTemplate.disabled ===
                                      "function"
                                        ? fieldTemplate.disabled(form, field)
                                        : fieldTemplate.disabled
                                    }
                                    placeholder={fieldTemplate.placeholder}
                                  />
                                ) : fieldTemplate.type === "multiselect-alt" ? (
                                  <MultiSelectAlt
                                    className={`${
                                      form.formState.errors[field.name] &&
                                      "border-red-500"
                                    }`}
                                    options={
                                      typeof fieldTemplate.options ===
                                      "function"
                                        ? fieldTemplate.options(form, field)
                                        : (fieldTemplate.options ?? [])
                                    }
                                    onChange={field.onChange}
                                    selected={field.value || []}
                                    disabled={
                                      typeof fieldTemplate.disabled ===
                                      "function"
                                        ? fieldTemplate.disabled(form, field)
                                        : fieldTemplate.disabled
                                    }
                                    placeholder={fieldTemplate.placeholder}
                                  />
                                ) : fieldTemplate.type === "select" ? (
                                  <Select
                                    onValueChange={field.onChange}
                                    value={field.value}
                                    disabled={
                                      !(
                                        typeof fieldTemplate.options ===
                                        "function"
                                          ? fieldTemplate.options(form, field)
                                          : fieldTemplate.options
                                      )?.length ||
                                      (typeof fieldTemplate.disabled ===
                                      "function"
                                        ? fieldTemplate.disabled(form, field)
                                        : fieldTemplate.disabled)
                                    }
                                  >
                                    <SelectTrigger
                                      className={`${
                                        form.formState.errors[field.name] &&
                                        "border-red-500"
                                      }`}
                                    >
                                      <SelectValue
                                        placeholder={fieldTemplate.placeholder}
                                      />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {(typeof fieldTemplate.options ===
                                      "function"
                                        ? fieldTemplate.options(form, field)
                                        : fieldTemplate.options
                                      )?.map((option) => (
                                        <SelectItem value={option.value}>
                                          {option.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : fieldTemplate.type === "select-search" ? (
                                  <SelectSearch
                                    options={
                                      typeof fieldTemplate.options ===
                                      "function"
                                        ? fieldTemplate.options(form, field)
                                        : (fieldTemplate.options ?? [])
                                    }
                                    value={field.value}
                                    onChange={field.onChange}
                                    disabled={
                                      typeof fieldTemplate.disabled ===
                                      "function"
                                        ? fieldTemplate.disabled(form, field)
                                        : fieldTemplate.disabled
                                    }
                                    placeholder={fieldTemplate.placeholder}
                                    className={`${
                                      form.formState.errors[field.name] &&
                                      "border-red-500"
                                    }`}
                                    triggerClassName={`${
                                      form.formState.errors[field.name] &&
                                      "border-red-500"
                                    }`}
                                    {...((fieldTemplate.extraProps?.(
                                      form,
                                      field,
                                    ) as Omit<
                                      React.ComponentPropsWithoutRef<
                                        typeof SelectSearch
                                      >,
                                      "options" | "onChange"
                                    >) || {})}
                                  />
                                ) : fieldTemplate.type === "dropzone" ? (
                                  <DropzoneField
                                    form={form as UseFormReturn<FieldValues>}
                                    field={
                                      field as ControllerRenderProps<
                                        FieldValues,
                                        string
                                      >
                                    }
                                    {...fieldTemplate.dropzoneOptions}
                                  />
                                ) : fieldTemplate.type === "drag-drop-file" ? (
                                  <FileUploadDropzone
                                    file={field.value || null}
                                    onFileSelect={(file) => {
                                      field.onChange(file);
                                      // Clear field error when file changes
                                      if (form.formState.errors[field.name]) {
                                        form.clearErrors(field.name as Path<FV>);
                                      }
                                    }}
                                    disabled={
                                      typeof fieldTemplate.disabled ===
                                      "function"
                                        ? fieldTemplate.disabled(form, field)
                                        : fieldTemplate.disabled
                                    }
                                    description={fieldTemplate.placeholder}
                                  />
                                ) : fieldTemplate.type === "formattedNumber" ? (
                                  <FormattedNumberField<FV>
                                    field={field}
                                    form={form}
                                    className={
                                      fieldTemplate.className as string
                                    }
                                    leftIcon={fieldTemplate.leftIcon}
                                  />
                                ) : fieldTemplate.type === "decimalInput" ? (
                                  <DecimalInput
                                    className={`${
                                      form.formState.errors[field.name] &&
                                      "border-red-500"
                                    }`}
                                    leftIcon={fieldTemplate.leftIcon}
                                    rightIcon={fieldTemplate.rightIcon}
                                    placeholder={fieldTemplate.placeholder}
                                    {...field}
                                    disabled={
                                      typeof fieldTemplate.disabled ===
                                      "function"
                                        ? fieldTemplate.disabled(form, field)
                                        : fieldTemplate.disabled
                                    }
                                    {...((fieldTemplate.extra &&
                                      fieldTemplate.extra) as React.ComponentPropsWithoutRef<
                                      typeof Input
                                    >)}
                                    {...(fieldTemplate.extraProps?.(
                                      form,
                                      field,
                                    ) as React.ComponentPropsWithoutRef<
                                      typeof Input
                                    >)}
                                  />
                                ) : (
                                  <Input
                                    className={`${
                                      form.formState.errors[field.name] &&
                                      "border-red-500"
                                    }`}
                                    leftIcon={fieldTemplate.leftIcon}
                                    rightIcon={fieldTemplate.rightIcon}
                                    placeholder={fieldTemplate.placeholder}
                                    type={fieldTemplate.type || "text"}
                                    {...field}
                                    disabled={
                                      typeof fieldTemplate.disabled ===
                                      "function"
                                        ? fieldTemplate.disabled(form, field)
                                        : fieldTemplate.disabled
                                    }
                                    {...((fieldTemplate.extra &&
                                      fieldTemplate.extra) as React.ComponentPropsWithoutRef<
                                      typeof Input
                                    >)}
                                    {...(fieldTemplate.type === "file" && {
                                      onClick: (e: any) => {
                                        e.target.value = null;
                                      },
                                      onChange: (e) => {
                                        field.onChange(
                                          e.target.files
                                            ? e.target.files[0]
                                            : null,
                                        );
                                      },
                                      value: field.value?.fileName,
                                    })}
                                    {...(fieldTemplate.extraProps?.(
                                      form,
                                      field,
                                    ) as React.ComponentPropsWithoutRef<
                                      typeof Input
                                    >)}
                                    {...(fieldTemplate.step &&
                                      fieldTemplate.type === "number" && {
                                        step: fieldTemplate.step,
                                      })}
                                  />
                                )}
                              </FormControl>
                              {/* error message: */}
                              {fieldTemplate.helpText && (
                                <p className="mt-2 text-sm text-gray-500">
                                  {typeof fieldTemplate.helpText === "function"
                                    ? fieldTemplate.helpText(form)
                                    : fieldTemplate.helpText}
                                </p>
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                    </div>
                  </ConditionalWrapper>
                ))}
              </ConditionalWrapper>
            </div>
          ))}
          {showSubmitButton && (
            <div className={cn(ROW_CLASS, submitButtonClassName)}>
              <Button className="col-span-12" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {submitLabel || "Submit"}
              </Button>
            </div>
          )}
          {showErrors && form.formState.errors.root && (
            <div className="col-span-12 mt-2 text-center text-sm text-red-500">
              {form.formState.errors.root ? (
                form.formState.errors.root.message
              ) : (
                <EmptySpace />
              )}
            </div>
          )}
        </div>
      </form>
    </Form>
  );
};

const FormTemplate = React.forwardRef(FormTemplateInner) as <
  FV extends FieldValues,
>(
  props: FormTemplateProps<FV> & { ref?: React.ForwardedRef<any> },
) => ReturnType<typeof FormTemplateInner>;

export default FormTemplate;
